import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Download, 
  Loader2, 
  Eye, 
  Trash2, 
  MessageSquare,
  RefreshCw,
  FlaskConical,
  Package,
  CheckCircle2,
  Clock,
  AlertCircle,
  Search,
  X,
  FileText,
  Send,
  PauseCircle,
  XCircle,
  Factory,
  ChevronDown,
  ChevronUp,
  ExternalLink
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  draft: { label: "Borrador", color: "bg-gray-500/20 text-gray-400", icon: Clock },
  submitted: { label: "Enviado", color: "bg-blue-500/20 text-blue-400", icon: Send },
  under_review: { label: "En revisión", color: "bg-yellow-500/20 text-yellow-400", icon: Eye },
  approved: { label: "Aprobado", color: "bg-green-500/20 text-green-400", icon: CheckCircle2 },
  in_production: { label: "En producción", color: "bg-purple-500/20 text-purple-400", icon: Factory },
  completed: { label: "Completado", color: "bg-emerald-500/20 text-emerald-400", icon: CheckCircle2 },
  rejected: { label: "Rechazado", color: "bg-red-500/20 text-red-400", icon: XCircle },
};

const FILE_TYPE_LABELS = {
  formula: "Fórmula",
  tech_sheet: "Ficha técnica",
  ingredients: "Ingredientes",
  nutritional: "Tabla nutricional",
  process: "Proceso",
  brand_manual: "Manual de marca",
  logo: "Logotipo",
  label: "Etiqueta",
  other: "Otro",
};

export default function AdminPage() {
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedProject, setSelectedProject] = useState(null);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    beverage: true,
    packaging: false,
    brand: false,
    production: false,
    files: false,
    notes: false,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [projectsRes, statsRes] = await Promise.all([
        axios.get(`${API}/projects`),
        axios.get(`${API}/stats`)
      ]);
      setProjects(projectsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error("Error al cargar datos");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (projectId, status) => {
    try {
      await axios.patch(`${API}/projects/${projectId}/status?status=${status}`);
      toast.success("Estado actualizado");
      fetchData();
      if (selectedProject?.id === projectId) {
        setSelectedProject(prev => ({ ...prev, status }));
      }
    } catch (err) {
      toast.error("Error al actualizar estado");
    }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm("¿Seguro que quieres eliminar este proyecto?")) return;
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      toast.success("Proyecto eliminado");
      fetchData();
      setSelectedProject(null);
    } catch (err) {
      toast.error("Error al eliminar proyecto");
    }
  };

  const downloadDossier = async (projectId, brandName) => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/dossier`, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `dossier_${brandName || "proyecto"}_${projectId.slice(0,8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Dossier descargado");
    } catch (err) {
      toast.error("Error al descargar dossier");
    }
  };

  const downloadFile = async (projectId, fileId, filename) => {
    try {
      // Since files are stored as base64 in the project, we need to fetch the project first
      const project = await axios.get(`${API}/projects/${projectId}`);
      const attachment = project.data.attachments?.find(a => a.id === fileId);
      if (attachment && attachment.data) {
        const byteCharacters = atob(attachment.data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: attachment.content_type });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success("Archivo descargado");
      } else {
        toast.error("Archivo no encontrado");
      }
    } catch (err) {
      toast.error("Error al descargar archivo");
      console.error(err);
    }
  };

  const addNote = async () => {
    if (!newNote.trim() || !selectedProject) return;
    setAddingNote(true);
    try {
      await axios.post(`${API}/projects/${selectedProject.id}/admin-notes`, {
        content: newNote,
        author: "Admin"
      });
      toast.success("Nota añadida");
      setNewNote("");
      // Refresh project
      const res = await axios.get(`${API}/projects/${selectedProject.id}`);
      setSelectedProject(res.data);
      fetchData();
    } catch (err) {
      toast.error("Error al añadir nota");
    } finally {
      setAddingNote(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const filteredProjects = projects.filter(p => {
    const matchesSearch = 
      p.brand?.brand_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.contact_company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const SectionHeader = ({ id, title, icon: Icon }) => (
    <CollapsibleTrigger 
      className="flex items-center justify-between w-full py-3 border-b border-udl-gray-800"
      onClick={() => toggleSection(id)}
    >
      <div className="flex items-center gap-2">
        <Icon size={16} className="text-udl-primary" />
        <span className="text-udl-primary font-heading font-bold text-sm uppercase tracking-wider">{title}</span>
      </div>
      {expandedSections[id] ? <ChevronUp size={16} className="text-udl-gray-400" /> : <ChevronDown size={16} className="text-udl-gray-400" />}
    </CollapsibleTrigger>
  );

  const DataRow = ({ label, value }) => (
    <div className="flex justify-between py-1.5 border-b border-udl-gray-800/50">
      <span className="text-udl-gray-400 text-sm">{label}</span>
      <span className="text-udl-white text-sm text-right max-w-[60%]">{value || "-"}</span>
    </div>
  );

  return (
    <main className="min-h-screen pt-20 bg-udl-bg" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 lg:px-12 py-8 lg:py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl lg:text-3xl font-bold text-udl-white mb-2">Panel Admin</h1>
            <p className="text-udl-gray-400 text-sm">Gestión de proyectos industriales</p>
          </div>
          <button 
            onClick={fetchData} 
            className="btn-secondary flex items-center gap-2"
            data-testid="refresh-btn"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Actualizar
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8" data-testid="stats-grid">
            <div className="bg-udl-surface border border-udl-gray-800 p-4">
              <p className="text-udl-gray-400 text-xs uppercase tracking-wider mb-1">Total</p>
              <p className="text-udl-white font-heading text-2xl font-bold">{stats.total}</p>
            </div>
            <div className="bg-udl-surface border border-udl-gray-800 p-4">
              <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Borradores</p>
              <p className="text-udl-white font-heading text-2xl font-bold">{stats.drafts}</p>
            </div>
            <div className="bg-udl-surface border border-udl-gray-800 p-4">
              <p className="text-blue-400 text-xs uppercase tracking-wider mb-1">Enviados</p>
              <p className="text-udl-white font-heading text-2xl font-bold">{stats.submitted}</p>
            </div>
            <div className="bg-udl-surface border border-udl-gray-800 p-4">
              <p className="text-yellow-400 text-xs uppercase tracking-wider mb-1">En revisión</p>
              <p className="text-udl-white font-heading text-2xl font-bold">{stats.under_review}</p>
            </div>
            <div className="bg-udl-surface border border-udl-gray-800 p-4">
              <p className="text-green-400 text-xs uppercase tracking-wider mb-1">Aprobados</p>
              <p className="text-udl-white font-heading text-2xl font-bold">{stats.approved}</p>
            </div>
            <div className="bg-udl-surface border border-udl-gray-800 p-4">
              <p className="text-purple-400 text-xs uppercase tracking-wider mb-1">Producción</p>
              <p className="text-udl-white font-heading text-2xl font-bold">{stats.in_production}</p>
            </div>
            <div className="bg-udl-surface border border-udl-gray-800 p-4">
              <p className="text-emerald-400 text-xs uppercase tracking-wider mb-1">Completados</p>
              <p className="text-udl-white font-heading text-2xl font-bold">{stats.completed}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-udl-gray-700" size={18} />
            <Input 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por marca, contacto, empresa o ID..."
              className="pl-10 bg-udl-surface border-udl-gray-800 text-udl-white"
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-48 bg-udl-surface border-udl-gray-800 text-udl-white" data-testid="status-filter">
              <SelectValue placeholder="Filtrar por estado" />
            </SelectTrigger>
            <SelectContent className="bg-udl-surface border-udl-gray-800">
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value="draft">Borrador</SelectItem>
              <SelectItem value="submitted">Enviado</SelectItem>
              <SelectItem value="under_review">En revisión</SelectItem>
              <SelectItem value="approved">Aprobado</SelectItem>
              <SelectItem value="in_production">En producción</SelectItem>
              <SelectItem value="completed">Completado</SelectItem>
              <SelectItem value="rejected">Rechazado</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Projects Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-udl-primary" size={32} />
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="text-center py-20 bg-udl-surface border border-udl-gray-800">
            <Package className="mx-auto text-udl-gray-700 mb-4" size={48} />
            <p className="text-udl-gray-400">No hay proyectos</p>
          </div>
        ) : (
          <div className="bg-udl-surface border border-udl-gray-800 overflow-hidden" data-testid="projects-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-udl-gray-800">
                    <th className="text-left p-4 text-udl-gray-400 text-xs uppercase tracking-wider">Proyecto</th>
                    <th className="text-left p-4 text-udl-gray-400 text-xs uppercase tracking-wider hidden md:table-cell">Categoría</th>
                    <th className="text-left p-4 text-udl-gray-400 text-xs uppercase tracking-wider hidden lg:table-cell">Envase</th>
                    <th className="text-left p-4 text-udl-gray-400 text-xs uppercase tracking-wider">Contacto</th>
                    <th className="text-left p-4 text-udl-gray-400 text-xs uppercase tracking-wider">Estado</th>
                    <th className="text-left p-4 text-udl-gray-400 text-xs uppercase tracking-wider hidden md:table-cell">Fecha</th>
                    <th className="text-right p-4 text-udl-gray-400 text-xs uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects.map((project) => {
                    const StatusIcon = STATUS_CONFIG[project.status]?.icon || Clock;
                    return (
                      <tr key={project.id} className="border-b border-udl-gray-800 hover:bg-udl-card transition-colors">
                        <td className="p-4">
                          <p className="text-udl-white font-medium">{project.brand?.brand_name || "Sin nombre"}</p>
                          <p className="text-udl-gray-700 text-xs font-mono">{project.id.slice(0, 12)}...</p>
                          {project.is_draft && (
                            <span className="text-xs text-gray-500 italic">Borrador</span>
                          )}
                        </td>
                        <td className="p-4 hidden md:table-cell">
                          <span className="text-udl-gray-400 text-sm capitalize">{project.beverage?.category || "-"}</span>
                          {project.beverage?.subcategory && (
                            <p className="text-udl-gray-700 text-xs">{project.beverage.subcategory}</p>
                          )}
                        </td>
                        <td className="p-4 hidden lg:table-cell">
                          <span className="text-udl-gray-400 text-sm">{project.packaging?.sku || "-"}</span>
                          {project.packaging?.volume && (
                            <p className="text-udl-primary text-xs">{project.packaging.volume}</p>
                          )}
                        </td>
                        <td className="p-4">
                          <p className="text-udl-white text-sm">{project.contact_name || "-"}</p>
                          <p className="text-udl-gray-700 text-xs truncate max-w-[120px]">{project.contact_company || ""}</p>
                        </td>
                        <td className="p-4">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium ${STATUS_CONFIG[project.status]?.color || "bg-gray-500/20 text-gray-400"}`}>
                            <StatusIcon size={12} />
                            <span className="hidden sm:inline">{STATUS_CONFIG[project.status]?.label || project.status}</span>
                          </span>
                        </td>
                        <td className="p-4 hidden md:table-cell">
                          <span className="text-udl-gray-400 text-sm">
                            {project.created_at ? new Date(project.created_at).toLocaleDateString("es-ES") : "-"}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => setSelectedProject(project)}
                              className="p-2 text-udl-gray-400 hover:text-udl-white transition-colors"
                              title="Ver detalles"
                              data-testid={`view-${project.id}`}
                            >
                              <Eye size={16} />
                            </button>
                            <button
                              onClick={() => downloadDossier(project.id, project.brand?.brand_name)}
                              className="p-2 text-udl-gray-400 hover:text-udl-primary transition-colors"
                              title="Descargar PDF"
                              data-testid={`download-${project.id}`}
                            >
                              <Download size={16} />
                            </button>
                            <button
                              onClick={() => deleteProject(project.id)}
                              className="p-2 text-udl-gray-400 hover:text-red-400 transition-colors"
                              title="Eliminar"
                              data-testid={`delete-${project.id}`}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Project Detail Modal */}
        {selectedProject && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/80" data-testid="project-modal">
            <div className="bg-udl-surface border border-udl-gray-800 max-w-5xl w-full max-h-[95vh] overflow-hidden flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 md:p-6 border-b border-udl-gray-800 shrink-0">
                <div>
                  <h3 className="text-udl-white font-heading font-bold text-lg md:text-xl">
                    {selectedProject.brand?.brand_name || "Proyecto sin nombre"}
                  </h3>
                  <p className="text-udl-gray-400 text-xs md:text-sm font-mono">{selectedProject.id}</p>
                </div>
                <button 
                  onClick={() => setSelectedProject(null)} 
                  className="text-udl-gray-400 hover:text-udl-white p-2"
                  data-testid="close-modal-btn"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Modal Content */}
              <ScrollArea className="flex-1">
                <div className="p-4 md:p-6 space-y-6">
                  {/* Status Update */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4 p-4 bg-udl-card border border-udl-gray-800">
                    <div className="flex-1">
                      <Label className="text-udl-gray-400 text-xs mb-1 block">Estado del proyecto</Label>
                      <Select value={selectedProject.status} onValueChange={(v) => updateStatus(selectedProject.id, v)}>
                        <SelectTrigger className="bg-udl-surface border-udl-gray-800 text-udl-white" data-testid="update-status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-udl-surface border-udl-gray-800">
                          <SelectItem value="draft">Borrador</SelectItem>
                          <SelectItem value="submitted">Enviado</SelectItem>
                          <SelectItem value="under_review">En revisión</SelectItem>
                          <SelectItem value="approved">Aprobado</SelectItem>
                          <SelectItem value="in_production">En producción</SelectItem>
                          <SelectItem value="completed">Completado</SelectItem>
                          <SelectItem value="rejected">Rechazado</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {selectedProject.submitted_at && (
                      <div className="text-right">
                        <p className="text-udl-gray-700 text-xs">Enviado</p>
                        <p className="text-udl-gray-400 text-sm">{new Date(selectedProject.submitted_at).toLocaleString("es-ES")}</p>
                      </div>
                    )}
                  </div>

                  {/* Warnings */}
                  {selectedProject.warnings?.length > 0 && (
                    <div className="p-4 bg-yellow-500/10 border border-yellow-500/30">
                      <p className="text-yellow-400 font-medium text-sm mb-2">Alertas de compatibilidad</p>
                      {selectedProject.warnings.map((w, i) => (
                        <p key={i} className="text-yellow-400/80 text-sm">{w}</p>
                      ))}
                    </div>
                  )}

                  {/* Beverage Info */}
                  <Collapsible open={expandedSections.beverage}>
                    <SectionHeader id="beverage" title="Brief de Bebida" icon={FlaskConical} />
                    <CollapsibleContent className="pt-4 space-y-1">
                      <DataRow label="Tipo proyecto" value={selectedProject.beverage?.project_type} />
                      <DataRow label="Categoría" value={selectedProject.beverage?.category} />
                      <DataRow label="Subcategoría" value={selectedProject.beverage?.subcategory} />
                      <DataRow label="Carbonatación" value={selectedProject.beverage?.carbonation} />
                      <DataRow label="CO2 (vol)" value={selectedProject.beverage?.co2_volumes} />
                      <DataRow label="Azúcar" value={selectedProject.beverage?.sugar_type} />
                      <DataRow label="Edulcorante" value={selectedProject.beverage?.sweetener_type} />
                      <DataRow label="Dulzor" value={`${selectedProject.beverage?.sweetness_level || 5}/10`} />
                      <DataRow label="Sabor principal" value={selectedProject.beverage?.main_flavor} />
                      <DataRow label="Notas secundarias" value={selectedProject.beverage?.secondary_notes} />
                      <DataRow label="Acidez" value={selectedProject.beverage?.acidity} />
                      <DataRow label="Amargor" value={selectedProject.beverage?.bitterness} />
                      <DataRow label="Sensación boca" value={selectedProject.beverage?.mouthfeel} />
                      <DataRow label="Cafeína (mg)" value={selectedProject.beverage?.caffeine_mg} />
                      <DataRow label="Proteína (g)" value={selectedProject.beverage?.protein_g} />
                      <DataRow label="Vitaminas" value={selectedProject.beverage?.vitamins?.join(", ")} />
                      <DataRow label="Electrolitos" value={selectedProject.beverage?.electrolytes ? "Sí" : "No"} />
                      <DataRow label="Clean label" value={selectedProject.beverage?.clean_label ? "Sí" : "No"} />
                      {selectedProject.beverage?.is_alcoholic && (
                        <>
                          <DataRow label="ABV (%)" value={selectedProject.beverage?.abv} />
                          <DataRow label="Base alcohólica" value={selectedProject.beverage?.alcohol_base} />
                          <DataRow label="Perfil alcohólico" value={selectedProject.beverage?.alcohol_profile} />
                        </>
                      )}
                      <DataRow label="Países" value={selectedProject.beverage?.countries?.join(", ")} />
                      <DataRow label="Idiomas etiqueta" value={selectedProject.beverage?.label_languages?.join(", ")} />
                      <DataRow label="Certificaciones" value={selectedProject.beverage?.certifications?.join(", ")} />
                      <DataRow label="Posicionamiento" value={selectedProject.beverage?.positioning} />
                      <DataRow label="Público objetivo" value={selectedProject.beverage?.target_audience} />
                      <DataRow label="Competidores" value={selectedProject.beverage?.competitors} />
                      {selectedProject.beverage?.custom_description && (
                        <div className="pt-3 mt-3 border-t border-udl-gray-800">
                          <p className="text-udl-gray-400 text-xs mb-1">Descripción:</p>
                          <p className="text-udl-white text-sm">{selectedProject.beverage.custom_description}</p>
                        </div>
                      )}
                    </CollapsibleContent>
                  </Collapsible>

                  {/* Packaging */}
                  <Collapsible open={expandedSections.packaging}>
                    <SectionHeader id="packaging" title="Envase y Cierre" icon={Package} />
                    <CollapsibleContent className="pt-4 space-y-1">
                      <DataRow label="SKU" value={selectedProject.packaging?.sku} />
                      <DataRow label="Tipo" value={selectedProject.packaging?.container_type} />
                      <DataRow label="Volumen" value={selectedProject.packaging?.volume} />
                      <DataRow label="Material" value={selectedProject.packaging?.material} />
                      <DataRow label="Cuello" value={selectedProject.packaging?.neck} />
                      <DataRow label="Decoración" value={selectedProject.packaging?.decoration_type} />
                      <DataRow label="Embalaje" value={selectedProject.packaging?.box_type} />
                      <div className="pt-3 mt-3 border-t border-udl-gray-800">
                        <DataRow label="Cierre" value={selectedProject.closure?.closure_name} />
                        <DataRow label="ID Cierre" value={selectedProject.closure?.closure_id} />
                        <DataRow label="Pack" value={selectedProject.closure?.pack_type} />
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  {/* Brand */}
                  <Collapsible open={expandedSections.brand}>
                    <SectionHeader id="brand" title="Marca" icon={FileText} />
                    <CollapsibleContent className="pt-4 space-y-1">
                      <DataRow label="Estado marca" value={selectedProject.brand?.brand_status} />
                      <DataRow label="Nombre" value={selectedProject.brand?.brand_name} />
                      <DataRow label="Generado IA" value={selectedProject.brand?.ai_generated ? "Sí" : "No"} />
                      <DataRow label="Claim" value={selectedProject.brand?.claim} />
                      <DataRow label="Estilo" value={selectedProject.brand?.style} />
                      <DataRow label="Tono" value={selectedProject.brand?.tone} />
                      <DataRow label="Denominación" value={selectedProject.brand?.product_denomination} />
                      <DataRow label="Contenido neto" value={selectedProject.brand?.net_content} />
                      <DataRow label="País origen" value={selectedProject.brand?.country_origin} />
                      <DataRow label="EAN" value={selectedProject.brand?.ean_code} />
                    </CollapsibleContent>
                  </Collapsible>

                  {/* Production */}
                  <Collapsible open={expandedSections.production}>
                    <SectionHeader id="production" title="Producción" icon={Factory} />
                    <CollapsibleContent className="pt-4 space-y-1">
                      <DataRow label="MOQ" value={selectedProject.production?.moq} />
                      <DataRow label="Primer lote" value={selectedProject.production?.first_batch} />
                      <DataRow label="Volumen anual" value={selectedProject.production?.annual_volume} />
                      <DataRow label="Fecha objetivo" value={selectedProject.production?.target_date} />
                      <DataRow label="Canal venta" value={selectedProject.production?.sales_channel} />
                      <DataRow label="Países distribución" value={selectedProject.production?.countries?.join(", ")} />
                      <DataRow label="Precio objetivo" value={selectedProject.production?.target_price} />
                      {selectedProject.production?.logistics_requirements && (
                        <div className="pt-3 mt-3 border-t border-udl-gray-800">
                          <p className="text-udl-gray-400 text-xs mb-1">Requisitos logísticos:</p>
                          <p className="text-udl-white text-sm">{selectedProject.production.logistics_requirements}</p>
                        </div>
                      )}
                      {selectedProject.production?.factory_notes && (
                        <div className="pt-3 mt-3 border-t border-udl-gray-800">
                          <p className="text-udl-gray-400 text-xs mb-1">Notas fábrica:</p>
                          <p className="text-udl-white text-sm">{selectedProject.production.factory_notes}</p>
                        </div>
                      )}
                    </CollapsibleContent>
                  </Collapsible>

                  {/* Contact */}
                  <div className="p-4 bg-udl-card border border-udl-gray-800">
                    <p className="text-udl-primary font-heading font-bold text-sm uppercase tracking-wider mb-3">Contacto</p>
                    <div className="grid md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-udl-gray-400">Nombre:</span>{" "}
                        <span className="text-udl-white">{selectedProject.contact_name || "-"}</span>
                      </div>
                      <div>
                        <span className="text-udl-gray-400">Email:</span>{" "}
                        <a href={`mailto:${selectedProject.contact_email}`} className="text-udl-primary hover:underline">
                          {selectedProject.contact_email || "-"}
                        </a>
                      </div>
                      <div>
                        <span className="text-udl-gray-400">Teléfono:</span>{" "}
                        <span className="text-udl-white">{selectedProject.contact_phone || "-"}</span>
                      </div>
                      <div>
                        <span className="text-udl-gray-400">Empresa:</span>{" "}
                        <span className="text-udl-white">{selectedProject.contact_company || "-"}</span>
                      </div>
                    </div>
                    <div className="flex gap-4 mt-3 text-xs">
                      <span className={selectedProject.data_consent ? "text-green-400" : "text-gray-500"}>
                        {selectedProject.data_consent ? "✓" : "✗"} Consentimiento datos
                      </span>
                      <span className={selectedProject.nda_accepted ? "text-green-400" : "text-gray-500"}>
                        {selectedProject.nda_accepted ? "✓" : "✗"} NDA solicitado
                      </span>
                    </div>
                  </div>

                  {/* Files */}
                  <Collapsible open={expandedSections.files}>
                    <SectionHeader id="files" title={`Archivos adjuntos (${selectedProject.attachments?.length || 0})`} icon={FileText} />
                    <CollapsibleContent className="pt-4">
                      {selectedProject.attachments?.length > 0 ? (
                        <div className="space-y-2">
                          {selectedProject.attachments.map((att) => (
                            <div key={att.id} className="flex items-center justify-between p-3 bg-udl-card border border-udl-gray-800">
                              <div className="flex items-center gap-3">
                                <FileText size={18} className="text-udl-primary" />
                                <div>
                                  <p className="text-udl-white text-sm">{att.filename}</p>
                                  <p className="text-udl-gray-700 text-xs">
                                    {FILE_TYPE_LABELS[att.file_type] || att.file_type}
                                    {att.size && ` · ${(att.size / 1024).toFixed(1)} KB`}
                                  </p>
                                </div>
                              </div>
                              <button
                                onClick={() => downloadFile(selectedProject.id, att.id, att.filename)}
                                className="p-2 text-udl-gray-400 hover:text-udl-primary transition-colors"
                                title="Descargar"
                              >
                                <Download size={16} />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-udl-gray-700 text-sm">No hay archivos adjuntos</p>
                      )}
                    </CollapsibleContent>
                  </Collapsible>

                  {/* Notes */}
                  <Collapsible open={expandedSections.notes}>
                    <SectionHeader id="notes" title={`Notas internas (${selectedProject.admin_notes?.length || 0})`} icon={MessageSquare} />
                    <CollapsibleContent className="pt-4">
                      <div className="space-y-3 mb-4">
                        {selectedProject.admin_notes?.length > 0 ? (
                          selectedProject.admin_notes.map((note, i) => (
                            <div key={note.id || i} className="p-3 bg-udl-card border border-udl-gray-800">
                              <p className="text-udl-white text-sm">{note.content}</p>
                              <p className="text-udl-gray-700 text-xs mt-2">
                                {note.author} · {new Date(note.created_at).toLocaleString("es-ES")}
                              </p>
                            </div>
                          ))
                        ) : (
                          <p className="text-udl-gray-700 text-sm">No hay notas</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Input 
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          placeholder="Añadir nota interna..."
                          className="flex-1 bg-udl-card border-udl-gray-800 text-udl-white"
                          data-testid="note-input"
                          onKeyDown={(e) => e.key === "Enter" && addNote()}
                        />
                        <button
                          onClick={addNote}
                          disabled={addingNote || !newNote.trim()}
                          className="btn-secondary flex items-center gap-2"
                          data-testid="add-note-btn"
                        >
                          {addingNote ? <Loader2 className="animate-spin" size={16} /> : <MessageSquare size={16} />}
                          Añadir
                        </button>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              </ScrollArea>

              {/* Modal Footer */}
              <div className="p-4 md:p-6 border-t border-udl-gray-800 flex flex-col md:flex-row justify-end gap-3 shrink-0">
                <button
                  onClick={() => downloadDossier(selectedProject.id, selectedProject.brand?.brand_name)}
                  className="btn-secondary flex items-center justify-center gap-2"
                  data-testid="modal-download-btn"
                >
                  <Download size={16} />
                  Descargar PDF
                </button>
                <button
                  onClick={() => deleteProject(selectedProject.id)}
                  className="px-4 py-2 text-red-400 hover:text-red-300 transition-colors flex items-center justify-center gap-2 border border-red-400/30 hover:border-red-400/50"
                  data-testid="modal-delete-btn"
                >
                  <Trash2 size={16} />
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
